import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import Decimal from 'decimal.js-light';
import { Observable, Subject, of } from 'rxjs';
import { CalcExchangeResponseViewModel, ExchangeCurrencyParams } from '../../../../model/src/public-api';
import { isNullOrEmpty } from '../../../../util/src/lib/is-null-or-empty';
import { AppConfig } from '../../../config/app.config';
import { ExchangeRateService } from '../../../core/services/exchange-rate.service';
import { InputNumberComponent } from '../input-number/input-number.component';

/**
 * @example 一般金額輸入(使用FormControl)
 * <sigv-currency
 *  [caseCurrency]="CurrentCurrency"
 *  [amount]="termsFormGroup.get('applyAmount')"
 *  (amountChange)="termsFormGroup.setValue($event)"
 *  [required]="true"
 *  (blurEvent)="blurEvent()"
 * ></sigv-currency>
 *
 * @example 一般金額輸入(使用Model)
 * <sigv-currency
 *  [caseCurrency]="CurrentCurrency"
 *  [(amount)]="applyAmount"
 *  [required]="true"
 *  (blurEvent)="blurEvent()"
 * ></sigv-currency>
 *
 * @example 金額唯讀
 * <sigv-currency
 *  [caseCurrency]="CurrentCurrency"
 *  [(amount)]="applyAmount"
 *  [isView]="true"
 * ></sigv-currency>
 *  * @example 換匯(即時)
 * <sigv-currency
 *  [(amount)]="applyAmount"
 *  [caseCurrency]="CurrentCurrency"
 *  [exchangeCurrency]="defaultCurrency"
 *  [doTransferCurrencyAmount]="true">
 * </sigv-currency>
 * @example 換匯(指定日期)
 * <sigv-currency
 *  [(amount)]="applyAmount"
 *  [caseCurrency]="CurrentCurrency"
 *  [exchangeCurrency]="defaultCurrency"
 *  quotedDate="2023-01-01"
 *  [doTransferCurrencyAmount]="true">
 * </sigv-currency>
 * @example 換匯(指定匯率)
 * <sigv-currency
 *  [(amount)]="applyAmount"
 *  [readonly]="true"
 *  [caseCurrency]="CurrentCurrency"
 *  [exchangeCurrency]="defaultCurrency"
 *  [specifyExchangeRate]="3000"
 *  [doTransferCurrencyAmount]="true">
 * </sigv-currency>
 */
@Component({
  selector: 'sigv-currency',
  templateUrl: './currency.component.html',
  styleUrls: ['./currency.component.scss'],
})
export class CurrencyComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('realInput') realInput?: InputNumberComponent;
  @ViewChild('viewInput') viewInput?: InputNumberComponent;
  //#region Inputs

  /**
   * 案件幣別
   */
  @Input() caseCurrency = '';

  /**
   * 轉換幣別
   */
  @Input() exchangeCurrency = '';

  /**
   * 是否轉換幣別
   */
  @Input() doTransferCurrencyAmount = false;

  /**
   * 是否必填
   */
  @Input() required = false;

  /**
   * 最大長度
   */
  @Input() maxLength = 14;

  /**
   *  Input 值改變時，model 的值是否及時同步，false 時 onBlur 才同步
   */
  @Input() syncModelValue = false;

  /**
   * 唯讀
   */
  @Input() isView = false;

  @Input() numberPosition: 'start' | 'between' | 'end' = 'start';

  /**
   * 取得匯率日期
   */
  @Input() quotedDate = '';

  /**
   * 自動換匯
   */
  @Input() autoExchange = false;

  /**
   * 指定匯率值
   */
  @Input() specifyExchangeRate?: number;

  /**
   * 公司別(若沒指定幣別會找該公司別設定的作帳幣別)
   */
  @Input() companyId?: number;

  /**
   * 是否顯示幣別
   */
  @Input() showCurrency: boolean = true;
  //#endregion

  //#region Outputs
  /**
   * 金額，雙向綁定
   */
  @Output() amountChange = new EventEmitter<number>();

  /**
   * on Blur Event
   */
  @Output() blurEvent = new EventEmitter<number>();

  /**
   * on Input Event
   */
  @Output() inputEvent = new EventEmitter<number>();

  /**
   * on Focus Event
   */
  @Output() focusEvent = new EventEmitter<number>();
  //#endregion

  //#region data
  viewModel?: number;

  caseCurrencyFormat: string;

  caseCurrencyFractionDigits: { minFractionDigits: number, maxFractionDigits: number };

  caseCurrencyExtension: { step: number; round: 'off' | 'up' | 'down' };

  exchangeCurrencyFormat: string;

  exchangeCurrencyFractionDigits: { minFractionDigits: number, maxFractionDigits: number };

  exchangeCurrencyExtension: { step: number; round: 'off' | 'up' | 'down' };

  private _amount?: number;
  private destroy$ = new Subject();
  //#endregion

  constructor(
    private appConfig: AppConfig,
    private exchangeService: ExchangeRateService
  ) {
    this.caseCurrencyFractionDigits =
      this.appConfig.getDecimalLengthByCurrencyCode(this.caseCurrency);
    this.caseCurrencyFormat = this.appConfig.getNumberFormatByCurrencyCode(
      this.caseCurrency
    );
    this.caseCurrencyExtension =
      this.appConfig.getCurrencyExtensionByCurrencyCode(this.caseCurrency);
    this.exchangeCurrencyFractionDigits =
      this.appConfig.getDecimalLengthByCurrencyCode(this.exchangeCurrency);
    this.exchangeCurrencyFormat = this.appConfig.getNumberFormatByCurrencyCode(
      this.exchangeCurrency
    );
    this.exchangeCurrencyExtension =
      this.appConfig.getCurrencyExtensionByCurrencyCode(this.exchangeCurrency);
  }

  /**
   * 金額，初始值為 undefined
   */
  @Input() set amount(value: number | undefined) {
    if (value === this._amount) {
      return;
    }

    this._amount = value;
    if (this.doTransferCurrencyAmount) {
      this.modelToViewModel();
    }
  }

  get amount(): number | undefined {
    return this._amount;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.caseCurrency && !changes.caseCurrency.firstChange) {
      this.caseCurrencyFractionDigits =
        this.appConfig.getDecimalLengthByCurrencyCode(this.caseCurrency);
      this.caseCurrencyFormat = this.appConfig.getNumberFormatByCurrencyCode(
        this.caseCurrency
      );
      this.caseCurrencyExtension =
        this.appConfig.getCurrencyExtensionByCurrencyCode(this.caseCurrency);

      if (this.autoExchange) {
        const previousCurrency = changes.caseCurrency.previousValue as string;
        const previousExtension =
          this.appConfig.getCurrencyExtensionByCurrencyCode(previousCurrency);
        const currentCurrency = changes.caseCurrency.currentValue as string;
        const currentExtension =
          this.appConfig.getCurrencyExtensionByCurrencyCode(currentCurrency);

        const srcSetting: CurrencySetting = {
          currency: previousCurrency,
          ...previousExtension,
        };
        const targetSetting: CurrencySetting = {
          currency: currentCurrency,
          ...currentExtension,
        };

        if (currentExtension) {
          this.doCurrencyExchange(
            this._amount,
            srcSetting,
            targetSetting
          ).subscribe((result) => {
            const value = result.amountList![0].amount;
            this.updateModel(value);

            if (this.doTransferCurrencyAmount) {
              this.modelToViewModel();
            }
          });
        } else {
          this.updateModel(this._amount);
          this.modelToViewModel();
        }
      }
    }

    if (changes.exchangeCurrency && !changes.exchangeCurrency.firstChange) {
      if (!isNullOrEmpty(this.exchangeCurrency)) {
        this.exchangeCurrencyFractionDigits =
          this.appConfig.getDecimalLengthByCurrencyCode(this.exchangeCurrency);
        this.exchangeCurrencyFormat =
          this.appConfig.getNumberFormatByCurrencyCode(this.exchangeCurrency);
        this.exchangeCurrencyExtension =
          this.appConfig.getCurrencyExtensionByCurrencyCode(
            this.exchangeCurrency
          );

        if (this.doTransferCurrencyAmount) {
          this.modelToViewModel();
        }
      } else {
        this.modelToViewModel();
      }
    }

    if (
      changes.doTransferCurrencyAmount &&
      !changes.doTransferCurrencyAmount.firstChange
    ) {
      if (this.doTransferCurrencyAmount) {
        this.modelToViewModel();
      }
    }
  }

  ngOnInit(): void {
    if (isNullOrEmpty(this.exchangeCurrency)) {
      this.exchangeCurrency = this.caseCurrency;
    }
    if (!isNullOrEmpty(this.exchangeCurrency)) {
      this.exchangeCurrencyFractionDigits =
        this.appConfig.getDecimalLengthByCurrencyCode(this.exchangeCurrency);
      this.exchangeCurrencyFormat =
        this.appConfig.getNumberFormatByCurrencyCode(this.exchangeCurrency);
      this.exchangeCurrencyExtension =
        this.appConfig.getCurrencyExtensionByCurrencyCode(
          this.exchangeCurrency
        );
    }

    if (!isNullOrEmpty(this.caseCurrency)) {
      this.caseCurrencyFractionDigits =
        this.appConfig.getDecimalLengthByCurrencyCode(this.caseCurrency);
      this.caseCurrencyFormat = this.appConfig.getNumberFormatByCurrencyCode(
        this.caseCurrency
      );
      this.caseCurrencyExtension =
        this.appConfig.getCurrencyExtensionByCurrencyCode(this.caseCurrency);
    }

    if (this.doTransferCurrencyAmount) {
      this.modelToViewModel();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onModelChange(event: number): void {
    const tmpValue = this.calculateRoundValue(
      this.caseCurrencyExtension.step,
      this.caseCurrencyExtension.round,
      this._amount
    );
    if (event !== tmpValue) {
      this.realInput?.updateInput(tmpValue, null, 'spin', null);
      this.realInput?.updateModel(null, tmpValue);
      return;
    }
    this.amountChange.emit(this._amount);
  }

  onViewModelChange(event: number) {
    const tmpValue = this.calculateRoundValue(
      this.exchangeCurrencyExtension.step,
      this.exchangeCurrencyExtension.round,
      this.viewModel
    );
    if (event !== tmpValue) {
      this.viewInput?.updateInput(tmpValue, null, 'spin', null);
      this.viewInput?.updateModel(null, tmpValue);
      return;
    }
    this.viewModelToModel();
  }

  viewModelToModel(): void {
    const srcSetting: CurrencySetting = {
      currency: this.caseCurrency,
      ...this.caseCurrencyExtension,
    };
    const targetSetting: CurrencySetting = {
      currency: this.exchangeCurrency,
      ...this.exchangeCurrencyExtension,
    };

    if (
      isNullOrEmpty(srcSetting.currency) ||
      isNullOrEmpty(targetSetting.currency)
    ) {
      this.updateModel(this.viewModel);
    } else {
      this.doCurrencyExchange(
        this.viewModel,
        srcSetting,
        targetSetting,
        1
      ).subscribe((result) => {
        const value = result.amountList![0].amount;
        this.updateModel(value);
      });
    }
  }

  isNullOrEmpty(value: any): boolean {
    return isNullOrEmpty(value);
  }

  private calculateRoundValue(
    step?: number,
    round?: string,
    value?: number
  ): number | undefined {
    if (isNullOrEmpty(step) || isNullOrEmpty(round) || isNullOrEmpty(value)) {
      return value;
    }

    let newValue,
      num = 0;
    if (isNullOrEmpty(value) === false) {
      switch (round) {
        case 'up': // 無條件進位
          num = Math.ceil(new Decimal(value!).dividedBy(step!).toNumber());
          break;
        case 'down': // 無條件捨去
          num = Math.floor(new Decimal(value!).dividedBy(step!).toNumber());
          break;
        case 'off': // 四捨五入
        default: // 預設
          num = Math.round(new Decimal(value!).dividedBy(step!).toNumber());
          break;
      }
      newValue = new Decimal(step!).mul(num).toNumber();
    }

    return newValue;
  }

  // 可自行指定幣別計算進位後的金額
  calculateRoundValueForCustom(
    currency: string,
    value?: number
  ): number | undefined {
    if (isNullOrEmpty(value)) {
      return value;
    }

    let round = this.appConfig.getCurrencyExtensionByCurrencyCode(currency).round ?? undefined,
      step = this.appConfig.getCurrencyExtensionByCurrencyCode(currency).step ?? undefined;

    return this.calculateRoundValue(step, round, value);
  }

  private modelToViewModel(): void {
    const srcSetting: CurrencySetting = {
      currency: this.caseCurrency,
      ...this.caseCurrencyExtension,
    };
    const targetSetting: CurrencySetting = {
      currency: this.exchangeCurrency,
      ...this.exchangeCurrencyExtension,
    };

    if (
      isNullOrEmpty(srcSetting.currency) ||
      isNullOrEmpty(targetSetting.currency)
    ) {
      this.viewModel = this._amount;
    } else {
      this.doCurrencyExchange(
        this._amount,
        srcSetting,
        targetSetting
      ).subscribe((result) => {
        const value = result.amountList![0].amount;
        this.viewModel = value;
      });
    }
  }

  private updateModel(value?: number): void {
    this._amount = value;
    this.amountChange.emit(value);
  }

  private doCurrencyExchange(
    amount?: number,
    src?: CurrencySetting,
    target?: CurrencySetting,
    direction?: number
  ): Observable<CalcExchangeResponseViewModel> {
    let observable: Observable<CalcExchangeResponseViewModel>;
    if (
      isNullOrEmpty(amount) ||
      isNullOrEmpty(src?.currency) ||
      isNullOrEmpty(target?.currency)
    ) {
      const defaultResult = this.exchangeService.getDefaultExchangeResult({
        amount,
      });
      observable = of(defaultResult);
    } else {
      const paramsObj: ExchangeCurrencyParams = {
        amount,
        srcCurrency: src?.currency,
        targetCurrency: target?.currency,
        exchangeDate: this.quotedDate,
        calcDirection: direction,
        specifyExchangeRate: this.specifyExchangeRate,
        companyId: this.companyId,
        step: direction === 1 ? src?.step : target?.step,
        round: direction === 1 ? src?.round : target?.round,
      };
      observable = this.exchangeService.getExchangeResult(paramsObj);
    }

    return observable;
  }
}

interface CurrencySetting {
  currency?: string,
  step?: number,
  round?: 'off' | 'down' | 'up'
}
